import { utcDayBounds, utcDayKey } from "@/lib/calendarDay";
import type { Booking } from "@/types/Booking";
import { endTimeToMinutes } from "@/utils/bookingTime";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const hhmmToMinutes = (hhmm: string): number => {
  const p = hhmm.padStart(4, "0");
  return parseInt(p.slice(0, 2), 10) * 60 + parseInt(p.slice(2, 4), 10);
};

export interface CancellationChargeResult {
  chargeable: boolean;
  occurrence?: Date;
}

/**
 * Client mirror of ember `earliestCancelledOccurrenceWithin24h`. Returns whether cancelling this
 * booking from `options.fromDate` onward (null = from genesis) would be charged under C1/C6:
 * chargeable iff the earliest CANCELLED occurrence has not yet ended AND starts within the next 24h.
 * Pure / advisory — the server remains the source of truth (wasChargedOnCancel).
 */
export function isCancellationChargeable(
  booking: Pick<Booking, "date" | "startTime" | "endTime" | "recurrence">,
  options: { fromDate?: Date | null; now?: Date } = {},
): CancellationChargeResult {
  const now = options.now ?? new Date();
  const in24h = new Date(now.getTime() + MS_PER_DAY);
  const startMins = hhmmToMinutes(booking.startTime);
  const endMins = booking.endTime ? endTimeToMinutes(booking.endTime, booking.startTime) : null;
  const exceptions = new Set(
    (booking.recurrence?.exceptions ?? []).map((d) => utcDayKey(new Date(d))),
  );

  // Lower bound on which occurrences are being cancelled (start-of-UTC-day). null = whole series.
  const lowerBound = options.fromDate ? utcDayBounds(new Date(options.fromDate)).start : null;

  // Build the occurrence's start/end INSTANT on `d`'s UTC calendar day. `d` is always a
  // UTC-day-anchored Date (the booking's UTC-midnight `date`, or a value stepped from it below
  // via setUTCDate/setUTCMonth) — Date.UTC keeps the day fixed regardless of viewer TZ, unlike
  // the previous local `setHours`, which reinterpreted the UTC-midnight day in browser-local time.
  const occInstant = (d: Date, mins: number): Date =>
    new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), Math.floor(mins / 60), mins % 60, 0, 0));
  const occStart = (d: Date): Date => occInstant(d, startMins);
  const occEnd = (d: Date): Date | null => {
    if (endMins === null) return null; // unknown end — treat as not-yet-ended
    return occInstant(d, endMins);
  };
  const isCancelled = (d: Date): boolean => {
    if (lowerBound && d.getTime() < lowerBound.getTime()) return false;
    return !exceptions.has(utcDayKey(d));
  };
  const within = (d: Date): boolean => {
    const end = occEnd(d);
    return (end === null || end > now) && occStart(d) <= in24h;
  };

  const bookingDate = booking.date instanceof Date ? booking.date : new Date(booking.date);
  const base = utcDayBounds(bookingDate).start;
  if (isCancelled(base) && within(base)) return { chargeable: true, occurrence: occStart(base) };

  const rec = booking.recurrence;
  if (rec && rec.frequency) {
    const frequency = rec.frequency;
    const interval = rec.interval ?? 1;
    const until = rec.until ? new Date(rec.until) : null;
    const max = rec.count && rec.count > 0 ? rec.count : 520;
    const current = new Date(base);
    let occurrenceCount = 1; // base is occurrence 1
    for (let i = 0; i < max; i++) {
      if (frequency === "daily") current.setUTCDate(current.getUTCDate() + interval);
      else if (frequency === "weekly") current.setUTCDate(current.getUTCDate() + 7 * interval);
      else if (frequency === "monthly") current.setUTCMonth(current.getUTCMonth() + interval);
      else break;
      if (until && current > until) break;
      occurrenceCount++;
      if (rec.count && occurrenceCount > rec.count) break;
      if (occStart(current) > in24h) break; // no later occurrence can start within the window
      if (isCancelled(current) && within(current)) return { chargeable: true, occurrence: occStart(current) };
    }
  }

  return { chargeable: false };
}
